import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { adminApi } from '@/api';
import {
  ActionMenu,
  ActionMenuItem,
  Alert,
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  Button,
  CardActions,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  ListCard,
  LoadingState,
  Modal,
  PageHeader,
  Pagination,
  SearchInput,
  SegmentedTabs,
  useToast,
} from '@/components/ui';
import { DocumentViewer } from '@/features/verification/DocumentViewer';
import { ADMIN_DOCUMENT_LABELS, DOCUMENT_TONES } from '@/features/verification/status';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate, formatFileSize, formatMonthYear } from '@/utils/format';

/**
 * Diplomas, certificates and CVs, checked by an admin.
 *
 * A diploma is checked against the education entry it is attached to -- does
 * it say this institution, this degree, these dates, this grade? -- so each
 * row carries the claim next to the file. A certificate (a course or
 * professional one, from the student's Skills page) is checked the same way
 * against its name, issuer, dates and credential ID. Uploading is the
 * student's request, so either is only ever here once there is a file.
 *
 * Diplomas and certificates share one tab: both are a file checked against
 * what the student typed, and the backend merges the two queues
 * (`kind=diploma&kind=certification`) so ordering and pagination are right.
 * Each row says which it is.
 *
 * A CV is Pending from upload until an admin decides; there is no asking.
 */
const TABS = [
  { key: 'resume', label: 'CVs', kinds: ['resume'] },
  { key: 'certificates', label: 'Certificates & diplomas', kinds: ['certification', 'diploma'] },
];

/**
 * `?kind=` as admin alerts and older links write it. A diploma or a
 * certificate opens the combined tab; anything else (or nothing) opens CVs.
 */
function tabFor(kindParam) {
  return ['diploma', 'certification', 'certificates'].includes(kindParam)
    ? 'certificates'
    : 'resume';
}

const STATUSES = ['PENDING', 'VERIFIED', 'REJECTED'];

/** A count across the kinds a tab lists. */
function countFor(counts, kinds, status) {
  return kinds.reduce((total, kind) => total + (counts?.[kind]?.[status] ?? 0), 0);
}

/** Says which kind a row in the combined tab is. */
const KIND_BADGES = { diploma: 'Diploma', certification: 'Certificate' };

/** How each kind is named in titles, toasts and buttons. */
const NOUNS = { diploma: 'diploma', certification: 'certificate', resume: 'CV' };

const EMPTY_ICONS = { certificates: 'idBadge', resume: 'fileText' };

const PAGE_SIZE = 20;

export function AdminDocumentVerificationsPage() {
  // The tab lives in the address, so an admin alert opens the right queue
  // (`?kind=diploma` and `?kind=certification` both open the combined tab).
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = tabFor(searchParams.get('kind'));
  const kinds = TABS.find((entry) => entry.key === tab).kinds;
  const setTab = (next) =>
    setSearchParams(next === 'resume' ? {} : { kind: 'certification' }, { replace: true });
  const [status, setStatus] = useState('PENDING');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rejecting, setRejecting] = useState(null);
  const [viewing, setViewing] = useState(null);

  const params = {
    kind: kinds,
    page,
    page_size: PAGE_SIZE,
    statuses: status,
    ...(search.trim() ? { q: search.trim() } : {}),
  };

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['admin-document-verifications', params],
    queryFn: () => adminApi.listDocumentVerifications(params),
    placeholderData: keepPreviousData,
  });

  const { data: counts } = useQuery({
    queryKey: ['admin-document-verification-counts'],
    queryFn: adminApi.getDocumentVerificationCounts,
  });

  const kindTabs = TABS.map((entry) => ({
    key: entry.key,
    label: entry.label,
    count: countFor(counts, entry.kinds, 'PENDING') || undefined,
  }));
  const statusTabs = STATUSES.map((key) => ({
    key,
    label: ADMIN_DOCUMENT_LABELS[key],
    count: countFor(counts, kinds, key) || undefined,
  }));

  const rows = data?.data ?? [];
  const meta = data?.meta;
  const filtered = Boolean(search.trim());

  return (
    // On a phone or tablet held upright the list card runs to the bottom of the
    // screen.
    <div className="flex flex-col portrait:flex-1">
      <PageHeader
        title="Verify documents"
        description="Check diplomas against the education they prove, certificates against the details the student gave, and CVs against the student behind them."
      />

      <SegmentedTabs
        tabs={kindTabs}
        value={tab}
        onChange={(key) => {
          setTab(key);
          setPage(1);
        }}
        label="Document type"
        className="mb-6"
      />

      {tab === 'certificates' && status === 'PENDING' && !filtered && (
        <Alert tone="info" title="What you are checking" className="mb-6">
          Open the file and compare it with the details beside it. For a diploma, the institution,
          degree, field of study, dates and grade must match the education entry; for a certificate,
          the name, issuer, dates and credential ID must match. Either must be in the student&apos;s
          name. Reject with a reason the student can act on — a blurry scan, a different name, a
          grade or date that does not match.
        </Alert>
      )}

      <ListCard
        fill
        className="flex-1"
        isRefreshing={isPlaceholderData}
        toolbarStart={
          <SegmentedTabs
            variant="plain"
            tabs={statusTabs}
            value={status}
            onChange={(key) => {
              setStatus(key);
              setPage(1);
            }}
            label="Review state"
          />
        }
        toolbarEnd={
          <SearchInput
            label="Search students"
            hideLabel
            size="sm"
            className="w-full sm:w-64"
            placeholder="Student name or email"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            onClear={() => {
              setSearch('');
              setPage(1);
            }}
          />
        }
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading documents…" />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Icon name={EMPTY_ICONS[tab]} size={22} />}
              title={status === 'PENDING' && !filtered ? 'Nothing waiting' : 'Nothing here'}
              description={
                filtered
                  ? 'No documents in this state match that search.'
                  : status === 'PENDING'
                    ? 'Every document sent for checking has been decided.'
                    : 'No documents in this state right now.'
              }
            />
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <BoxList>
          {rows.map((row) => (
            <DocumentRow
              key={row.id}
              row={row}
              onView={() => setViewing(row)}
              onReject={() => setRejecting(row)}
            />
          ))}
        </BoxList>
      </ListCard>

      <RejectDocumentModal row={rejecting} onClose={() => setRejecting(null)} />

      {/* Shown in the page, not downloaded. Each open is audited. */}
      <DocumentViewer
        open={viewing !== null}
        onClose={() => setViewing(null)}
        title={viewing ? `${viewing.student_name}'s ${NOUNS[viewing.kind] ?? 'document'}` : ''}
        fileName={viewing?.file_name}
        contentType={viewing?.content_type}
        queryKey={[viewing?.kind, viewing?.id]}
        loadUrl={(options) => adminApi.getDocumentDownloadUrl(viewing.kind, viewing.id, options)}
      />
    </div>
  );
}

function useInvalidateConsole() {
  const queryClient = useQueryClient();
  // The status shows on this queue, its counts and the student record.
  return () =>
    void queryClient.invalidateQueries({
      predicate: (query) => String(query.queryKey[0]).startsWith('admin-'),
    });
}

function DocumentRow({ row, onView, onReject }) {
  const toast = useToast();
  const invalidate = useInvalidateConsole();
  const isDiploma = row.kind === 'diploma';
  const isCertification = row.kind === 'certification';
  const noun = NOUNS[row.kind] ?? 'document';

  const approve = useMutation({
    mutationFn: () => adminApi.approveDocument(row.kind, row.id),
    onSuccess: () => {
      toast.success(`${row.student_name}'s ${noun} is verified`);
      invalidate();
    },
    onError: (error) => toast.error(errorMessage(error, `Could not verify the ${noun}`)),
  });

  const period = isDiploma
    ? `${formatMonthYear(row.start_date)} – ${
        row.is_current ? 'Present' : formatMonthYear(row.end_date)
      }`
    : null;

  const certificateDates = isCertification
    ? [
        row.issue_date && `Issued ${formatDate(row.issue_date)}`,
        row.expiry_date && `expires ${formatDate(row.expiry_date)}`,
      ]
        .filter(Boolean)
        .join(' · ')
    : null;

  const facts = isDiploma
    ? [
        { icon: 'graduationCap', text: row.degree },
        { icon: 'university', text: row.institution_name },
        row.field_of_study && { icon: 'category', text: row.field_of_study },
        { icon: 'calendar', text: period },
        row.grade && { icon: 'star', text: `Grade ${row.grade}` },
      ]
    : isCertification
      ? [
          { icon: 'idBadge', text: row.certification_name },
          row.issuer && { icon: 'building', text: row.issuer },
          certificateDates && { icon: 'calendar', text: certificateDates },
          row.credential_id && { icon: 'tag', text: `ID ${row.credential_id}` },
        ]
      : [
          { icon: 'university', text: row.university ?? 'University not given' },
          row.is_primary && { icon: 'check', text: 'Primary CV', tone: 'success' },
          row.uploaded_at && { icon: 'upload', text: `Uploaded ${formatDate(row.uploaded_at)}` },
        ];

  return (
    <BoxRow
      accent={DOCUMENT_TONES[row.status] ?? 'neutral'}
      leading={<BoxAvatar name={row.student_name} round />}
      title={row.student_name}
      to={paths.admin.studentDetail(row.student_id)}
      subtitle={row.student_email}
      badges={
        <>
          {KIND_BADGES[row.kind] && (
            <Badge tone={isDiploma ? 'info' : 'brand'}>
              <Icon name={isDiploma ? 'graduationCap' : 'idBadge'} size={12} />
              {KIND_BADGES[row.kind]}
            </Badge>
          )}
          <Badge dot tone={DOCUMENT_TONES[row.status] ?? 'neutral'}>
            {ADMIN_DOCUMENT_LABELS[row.status] ?? row.status}
          </Badge>
          {row.university_email_verified && (
            <Badge tone="success">
              <Icon name="shieldCheck" size={12} />
              University email
            </Badge>
          )}
        </>
      }
      facts={[
        ...facts,
        {
          icon: 'fileText',
          text: `${row.file_name} · ${formatFileSize(row.size_bytes)}`,
        },
      ]}
      // When it was sent, and decided, sit quietly in the footer like the
      // dates on other console lists, rather than as chips among the facts.
      meta={
        (row.submitted_at ?? row.uploaded_at) || row.decided_at ? (
          <span className="text-ink-muted text-xs">
            {[
              (row.submitted_at ?? row.uploaded_at) &&
                `Submitted ${formatDate(row.submitted_at ?? row.uploaded_at)}`,
              row.decided_at && `Decided ${formatDate(row.decided_at)}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        ) : undefined
      }
      description={
        row.status === 'REJECTED' && row.rejection_reason
          ? `Rejected: ${row.rejection_reason}`
          : undefined
      }
      // Verify is the main action while the document is undecided; once it
      // is verified, reading it is. The student record (also the title link)
      // is the least used, so it goes in "More".
      actions={
        <CardActions>
          {row.status !== 'VERIFIED' && (
            <Button
              variant="secondary"
              size="sm"
              title={`Open ${row.student_name}'s ${noun}`}
              onClick={onView}
            >
              <Icon name="eye" size={14} />
              View {noun}
            </Button>
          )}
          {row.status !== 'REJECTED' && (
            <Button
              variant="tintDanger"
              size="sm"
              title={`Reject ${row.student_name}'s ${noun}`}
              onClick={onReject}
            >
              <Icon name="close" size={14} />
              Reject
            </Button>
          )}
          <ActionMenu label={`More actions for ${row.student_name}'s ${noun}`}>
            <ActionMenuItem
              icon="user"
              to={paths.admin.studentDetail(row.student_id)}
              title={`Open ${row.student_name}`}
            >
              Open student
            </ActionMenuItem>
          </ActionMenu>
          {row.status !== 'VERIFIED' ? (
            <Button
              variant="tintPositive"
              size="sm"
              title={`Verify ${row.student_name}'s ${noun}`}
              isLoading={approve.isPending}
              onClick={() => approve.mutate()}
            >
              <Icon name="check" size={14} />
              Verify
            </Button>
          ) : (
            <Button
              variant="tintBrand"
              size="sm"
              title={`Open ${row.student_name}'s ${noun}`}
              onClick={onView}
            >
              <Icon name="eye" size={14} />
              View {noun}
            </Button>
          )}
        </CardActions>
      }
    />
  );
}

function RejectDocumentModal({ row, onClose }) {
  const toast = useToast();
  const invalidate = useInvalidateConsole();
  const [reason, setReason] = useState('');
  const noun = NOUNS[row?.kind] ?? 'document';

  const reject = useMutation({
    mutationFn: (text) => adminApi.rejectDocument(row.kind, row.id, text),
    onSuccess: () => {
      toast.success(`The ${noun} was rejected`);
      invalidate();
      setReason('');
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error, `Could not reject the ${noun}`)),
  });

  return (
    <Modal
      open={row !== null}
      title={row ? `Reject ${row.student_name}'s ${noun}` : ''}
      description="The student is told the reason and can upload a new copy. It is recorded in the audit log."
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            // The backend asks for four characters.
            disabled={reason.trim().length < 4}
            isLoading={reject.isPending}
            onClick={() => reject.mutate(reason.trim())}
          >
            Reject
          </Button>
        </>
      }
    >
      <Input
        label="Reason"
        required
        placeholder={
          row?.kind === 'diploma'
            ? 'The grade on the diploma does not match the entry'
            : row?.kind === 'certification'
              ? 'The name on the certificate does not match the student'
              : 'The CV lists a degree that has not been verified'
        }
        value={reason}
        onChange={(event) => setReason(event.target.value)}
      />
    </Modal>
  );
}
